import React from "react";
import { graphql } from "gatsby";

import { Helmet } from "react-helmet";

import Layout from "../../../components/Layout";

import Hero from "../../../components/Hero";
import SideContentTemplate from "../../../components/ContentComponents/SideContentTemplate";
import PartialContentTemplate from "../../../components/ContentComponents/PartialContentTemplate";
import HereToHelpCard from "../../../components/HereToHelpCard";
import PartialPointList from "../../../components/PartialPointList";
import CoinLinkList from "../../../components/CoinLinkList";
import GuildLine from "../../../components/GuildLine";
import InvestAnywhereAnytime from "../../../components/ContentComponents/InvestAnywhereAnytime";
import Faq, { convertSupportCentreToFaqs } from "../../../components/Faq";
import WithI18next from "../../../components/WithI18next";

import { getAcfPartialContent, formatAcfPartial } from "../../../utils";

const TradeSideContentTemplate = ({
  data,
  pageContext: { heroSection },
  t,
}) => {
  const howToContent = getAcfPartialContent(data.howToContent);
  const hereToHelpContent = getAcfPartialContent(data.hereToHelpContent);

  return (
    <Layout
      title={t(`trade.seo.${heroSection}.title`)}
      description={t(`trade.seo.${heroSection}.description`)}
    >
      <Helmet>
        <body className="earn" />
      </Helmet>

      <Hero
        classNames={`coin-page ${heroSection.split("-")[1]}`}
        backgroundColor="blue"
        content={data.heroContent}
      />

      <GuildLine
        title={howToContent.pHeading}
        description={howToContent.pContent}
        steps={data.howToStepsContent.edges.map(
          ({ node: { acfPartials } }) => ({
            title: acfPartials.pHeading,
            description: acfPartials.pContent,
          })
        )}
        imgs={[
          data.imageTradeExchange.publicURL,
          data.imageWithdrawEarnings.publicURL,
        ]}
      />

      {data.aboutCoinContent && (
        <SideContentTemplate
          {...formatAcfPartial(data.aboutCoinContent)}
          backgroundImg={"secondary"}
          imgMaxWidth={450}
          isImgHiddenOnMobile={false}
        />
      )}

      <PartialContentTemplate {...formatAcfPartial(data.otherCoins)}>
        <CoinLinkList coins={data.otherCoinsLinks?.edges} />
      </PartialContentTemplate>

      {data.tradeCoinContent && (
        <SideContentTemplate
          {...formatAcfPartial(data.tradeCoinContent)}
          imgMaxWidth={450}
          signupBtnClassNames={"button-primary"}
          isContentReversed={true}
        />
      )}

      {data.whyUseZipmex && (
        <SideContentTemplate
          {...formatAcfPartial(data.whyUseZipmex)}
          isImgRotated={true}
          signupBtnClassNames={"bottom-link mb-2"}
        >
          <PartialPointList points={data.whyUseZipmexPoints.edges} />
        </SideContentTemplate>
      )}

      <InvestAnywhereAnytime content={data.investAnywhereContent} />

      <HereToHelpCard
        headerHTML={hereToHelpContent.pHeading}
        contentHTML={hereToHelpContent.pContent}
        btnLabel={hereToHelpContent.pButtonText}
        btnHref={hereToHelpContent.pButtonUrl}
      />

      <Faq
        content={convertSupportCentreToFaqs(data.faqsContent)}
        visitSupportUrl={"/support/"}
      />

      {data.ctaInvestContent && (
        <SideContentTemplate
          {...formatAcfPartial(data.ctaInvestContent)}
          backgroundColor="blue"
          signupBtnClassNames="button-secondary button-rounded"
          isContentReversed={true}
        />
      )}
    </Layout>
  );
};

export default WithI18next()(TradeSideContentTemplate);

export const query = graphql`
  query (
    $wpLocale: String!
    $wpGlobalConfig: String!
    $wpShareGlobalConfig: String!
    $heroSection: String!
    $howToSection: String!
    $howToStepsSection: String!
    $imageTradeExchange: String!
    $aboutCoinSection: String!
    $otherCoinSection: String!
    $tradeCoinSection: String!
    $whyUseZipmexSection: String!
    $hereToHelpSection: String!
    $ctaInvestSection: String!
  ) {
    heroContent: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: $heroSection } }
      }
    ) {
      edges {
        node {
          acfHeroSections {
            hsMainHeading
            hsMainText
            hsButtonText
            hsButtonLink
          }
        }
      }
    }

    howToContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $howToSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pContent
          }
        }
      }
    }

    howToStepsContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $howToStepsSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pContent
          }
        }
      }
    }

    imageCreateAccount: file(
      relativePath: { eq: "images/create-account.svg" }
    ) {
      publicURL
    }
    imageTradeExchange: file(relativePath: { eq: $imageTradeExchange }) {
      publicURL
    }
    imageWithdrawEarnings: file(
      relativePath: { eq: "images/withdraw-earnings.svg" }
    ) {
      publicURL
    }

    aboutCoinContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpShareGlobalConfig } }
        acfPartials: { pPartialId: { eq: $aboutCoinSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    otherCoins: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $otherCoinSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
          }
        }
      }
    }
    otherCoinsLinks: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "other-coins-links" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pButtonText
            pButtonUrl
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    tradeCoinContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $tradeCoinSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonUrl
            pButtonText
          }
        }
      }
    }

    whyUseZipmex: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $whyUseZipmexSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonText
            pButtonUrl
          }
        }
      }
    }
    whyUseZipmexPoints: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "why-use-zipmex-points" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    ...InvestAnytimeContent

    hereToHelpContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpShareGlobalConfig } }
        acfPartials: { pPartialId: { eq: $hereToHelpSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }

    faqsContent: allWpSupportCenterItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        categories: { nodes: { elemMatch: { slug: { eq: "general-faqs" } } } }
      }
      sort: { order: DESC, fields: date }
    ) {
      edges {
        node {
          title
          content
        }
      }
    }

    ctaInvestContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: $ctaInvestSection } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonText
            pButtonUrl
          }
        }
      }
    }
  }
`;
